import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API, useAuth, anonymize } from "../App";
import { GakEak } from "../utils/caseFormat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Scale, Calendar, FileText, User, AlertTriangle } from "lucide-react";
import { Skeleton } from "../components/ui/skeleton";

const JudgeReviews = () => {
  const { user } = useAuth();
  const privacy = user?.privacy_mode || false;
  const p = (text) => privacy ? anonymize(text) : text;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/judge/my-reviews`).then(res => {
      setData(res.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-24 w-full" />)}</div>;

  if (!data) return <p className="text-slate-500">Σφάλμα φόρτωσης</p>;

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" data-testid="reviews-title">
          <Scale className="w-6 h-6 text-indigo-600" />
          Οι Έλεγχοί Μου
        </h1>
        <p className="text-slate-500 mt-1">
          Δικαστής: <strong>{data.judge_name}</strong> | Σύνολο: <strong>{data.total_cases}</strong> υποθέσεις
        </p>
      </div>

      {/* Capacity Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {Object.entries(data.capacity).map(([ct, max]) => {
          const cases = data.cases_by_court[ct] || [];
          const pct = Math.round((cases.length / max) * 100);
          const isOverCap = cases.length >= max;
          return (
            <Card key={ct} className={isOverCap ? "border-red-300" : ""}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-800">{ct}</h3>
                  <Badge className={isOverCap ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}>
                    {cases.length} / {max}
                  </Badge>
                </div>
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div className={`h-2 rounded-full transition-all ${isOverCap ? "bg-red-500" : pct > 70 ? "bg-amber-500" : "bg-emerald-500"}`}
                    style={{ width: `${Math.min(pct, 100)}%` }} />
                </div>
                {isOverCap && (
                  <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Υπέρβαση χωρητικότητας
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Cases by Court Type */}
      {Object.entries(data.cases_by_court).map(([ct, cases]) => (
        <Card key={ct}>
          <CardHeader>
            <CardTitle className="text-lg">{ct} ({cases.length} υποθέσεις)</CardTitle>
            <CardDescription>Χωρητικότητα: {data.capacity[ct] || "N/A"} θέσεις</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {cases.map(c => {
                const pendingDeadlines = (c.deadlines || []).filter(d => d.status !== "completed");
                const nextDeadline = pendingDeadlines.sort((a, b) => (a.deadline_date || "").localeCompare(b.deadline_date || ""))[0];
                return (
                  <Link key={c.id} to={`/cases/${c.id}`}
                    className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100 hover:bg-slate-100 transition-colors"
                    data-testid={`review-case-${c.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                        <FileText className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-mono font-semibold text-sm">
                          <GakEak caseObj={c} />
                          {c.eak_number && <span className="text-indigo-600 ml-2">ΕΑΚ {c.eak_number}</span>}
                        </p>
                        <p className="text-xs text-slate-500">
                          {p(c.plaintiffs || "-")} κατά {p(c.defendants || "-")}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      {nextDeadline && (
                        <p className="text-xs text-slate-500">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          Επόμενη: {nextDeadline.deadline_date}
                        </p>
                      )}
                      <Badge className={
                        c.dispatch_status === "prodikastikos" ? "bg-indigo-100 text-indigo-800" :
                        "bg-slate-100 text-slate-600"
                      }>
                        {c.dispatch_status === "prodikastikos" ? "Προδικαστικός" : c.dispatch_status || "Αναμονή"}
                      </Badge>
                    </div>
                  </Link>
                );
              })}
              {cases.length === 0 && <p className="text-slate-500 text-sm text-center py-4">Δεν υπάρχουν υποθέσεις</p>}
            </div>
          </CardContent>
        </Card>
      ))}

      {data.total_cases === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <Scale className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-medium text-slate-700">Δεν υπάρχουν ανατεθειμένες υποθέσεις</h3>
            <p className="text-slate-500 mt-1">Δεν σας έχουν ανατεθεί υποθέσεις για προδικαστικό έλεγχο</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default JudgeReviews;
